import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { UserEmail, UserEmailMaxLength } from "../../../domain/user/UserEmail";
import { LabelValueSet } from "../../../domain/utility/LabelValueSet";
import { Paths } from "../../../Paths";
import { State, useAppDispatch } from "../../../state/store";
import { registerInquiryAction } from "../../../state/usecase/inquiry/actions";
import { useAuthUser } from "../../hooks/auth/useAuthUser";
import { useCareRecipientList } from "../../hooks/care_recipient/useCareRecipientList";
import { AdminOnlyComponents } from "../admin/components/AdminOnlyComponents";
import { Button } from "../common/button/Button";
import { FullScreenLoading } from "../common/FullScreenLoading";
import { InputSelectBox } from "../common/input/InputSelectBox";
import { InputTextArea } from "../common/input/InputTexArea";
import { InputTextBox } from "../common/input/InputTextBox";
import { useNavigate } from "react-router-dom";

export function InquiryFormPage() {

    const { careRecipients } = useCareRecipientList();
    const selectCareRecipients = careRecipients.map(
        careRecipient => new LabelValueSet(careRecipient.name.value, careRecipient.id.value)
    );
    const navigate = useNavigate();

    const user = useAuthUser();

    const [selectCareRecipient, setSelectCareRecipient] = useState(careRecipients[0]?.id.value ?? null);
    const [email, setEmail] = useState(user.email.value);
    const [tel, setTel] = useState("");
    const [inquiryType, setInquiryType] = useState("");
    const [content, setContent] = useState("");

    const [emailErrorMessage, setEmailErrorMessage] = useState("");
    const [telErrorMessage, setTelErrorMessage] = useState("");
    const [inquiryTypeErrorMessage, setInquiryTypeErrorMessage] = useState("");
    const [contentErrorMessage, setContentErrorMessage] = useState("");

    const { processingRegister } = useSelector((state: State) => state.inquiry);

    const dispatch = useAppDispatch();

    // const [processing, setProcessing] = useState(false);

    const setInitialize = useRef(false);
    useEffect(() => {
        if (careRecipients.length && !setInitialize.current) {
            setSelectCareRecipient(careRecipients[0].id.value);
            setInitialize.current = true;
        }
    }, [careRecipients, setInitialize]);


    const inquiryTypes = [
        new LabelValueSet("選択してください", ""),
        new LabelValueSet("サービスの使い方について", "サービスの使い方について"),
        new LabelValueSet("サービスへのご意見・ご要望", "サービスへのご意見・ご要望"),
        new LabelValueSet("その他", "その他")
    ];

    const clearErrorMessage = () => {
        setTelErrorMessage("");
        setInquiryTypeErrorMessage("");
        setContentErrorMessage("");
    };

    const isValidForm = () => {

        clearErrorMessage();
        let hasError =false;

        if (!UserEmail.fromString(email).isValid()
            || !UserEmail.fromString(email).isValidEmailNameLength()
            || !UserEmail.fromString(email).isValidEmailDomainLength()) {
            hasError = true;
            setEmailErrorMessage("メールアドレスの形式が正しくありません。");
        }
        if (!UserEmail.fromString(email).isValidLength()) {
            hasError = true;
            setEmailErrorMessage(`メールアドレスは${UserEmailMaxLength}文字以内で入力してください。`);
        }

        const regex = /^[0-9０-９\-ー]+$/;
        if (tel.length > 0 && !tel.match(regex)) {
            setTelErrorMessage("お電話番号は数字（0~9）および -（ハイフン）のみが入力できます");
            hasError = true;
        }

        if (inquiryType.length < 1) {
            setInquiryTypeErrorMessage("ご用件を選択してください");
            hasError = true;
        }

        if (content.length < 1) {
            setContentErrorMessage("お問い合わせ内容を入力してください");
            hasError = true;
        } else if (content.length > 1000) {
            setContentErrorMessage("お問い合わせ内容は1000文字未満で入力してください");
            hasError = true;
        }

        if (hasError) {
            return false;
        }
        return true;
    };

    const onClick = () => {
        if (!isValidForm()) return;
        dispatch(registerInquiryAction({
            careRecipientId: selectCareRecipient,
            tel: tel,
            category: inquiryType,
            content: content,
            email: email,
            successHandler: () => { navigate(Paths.inquiryCompleted); }
        }));
    };

    const onClickList = () => {
        navigate(`${Paths.admin}${Paths.inquiry}`);
    };

    return (
        <div className="inquiry_page">
            { processingRegister > 0 && <FullScreenLoading/> }
            <div className="container">
                <div className="title_area">
                    <h1 className="title">問い合わせ</h1>
                    <AdminOnlyComponents>
                        <Button label={"問い合わせ一覧へ"} onClick={onClickList}></Button>
                    </AdminOnlyComponents>
                </div>
                <div className="description">
                    <p>いつも居宅内モニタリングサービスをご利用いただき誠にありがとうございます。</p>
                    <p>皆さまからのご質問やご意見をお問い合わせフォームより受け付けております。</p>
                    <p>必要事項をご記入の上、「送信」ボタンを押してください。</p>
                </div>
                <div className="forms">
                    <div>
                        <InputSelectBox value={selectCareRecipient} list={selectCareRecipients ?? []} handleSelectBoxChanged={setSelectCareRecipient} label={"ご利用者名"} />
                    </div>
                    <div>
                        <InputTextBox value={email} handleTextChanged={setEmail} label={"連絡先メールアドレス"} hasError={emailErrorMessage.length > 0} maxLength={20} required/>
                        <div className="error_message">{emailErrorMessage}</div>
                    </div>
                    <div>
                        <InputTextBox value={tel} handleTextChanged={setTel} label={"お電話番号"} hasError={telErrorMessage.length > 0} maxLength={20}/>
                        <div className="error_message">{telErrorMessage}</div>
                    </div>
                    <div>
                        <InputSelectBox value={inquiryType} list={inquiryTypes} handleSelectBoxChanged={setInquiryType} label={"ご用件"} hasError={inquiryTypeErrorMessage.length > 0} required/>
                        <div className="error_message">{inquiryTypeErrorMessage}</div>
                    </div>
                    <div>
                        <InputTextArea value={content} handleTextChanged={setContent} label={"お問合せ内容"} width={400} hasError={contentErrorMessage.length > 0} maxLength={1000} required/>
                        <div className="error_message">{contentErrorMessage}</div>
                    </div>
                    <div className="button_area">
                        <Button label={"送信"} onClick={onClick} width={150}/>
                    </div>
                </div>
            </div>
        </div>
    );
}
